import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { tvApi } from "@/api/tv";
import {
  normalizeSeriesStatus,
  type MonitorPreset,
  type Series,
  type SeriesLookupResult,
  type SeriesStatus,
  type SeriesType,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, SearchInput, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";
import { SERIES_TYPE_LABEL, SERIES_TYPE_OPTIONS } from "@/lib/tv";

/**
 * TV series library — replaces the previous ComingSoon at /tv.
 *
 * Same poster-grid as movies, but each card carries the TMDB
 * "Continuing" / "Ended" status as a chip, and we sort by sort_title
 * with continuing-status weighted higher (so active shows surface
 * first when you scan the wall).
 */
/**
 * Status filter chips. `null` is the "all" sentinel — single-select
 * single-state, matching the issue spec: default shows everything.
 */
type StatusFilter = SeriesStatus | null;

/**
 * Issue #65: type filter chips. Same null = "all" sentinel as the
 * status filter; both filters AND together when both are set.
 */
type TypeFilter = SeriesType | null;

export function SeriesList() {
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(null);
  // Issue #66: when on, drop series whose seasons are all S00 (no real
  // seasons yet — usually a brand-new TMDB entry, or a docu-only feed
  // we don't really want cluttering the wall).
  const [hideSpecialsOnly, setHideSpecialsOnly] = useState(true);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { data: series, isLoading } = useQuery({
    queryKey: ["series"],
    queryFn: tvApi.list,
  });

  const lower = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!series) return [];
    const sorted = [...series].sort((a, b) => {
      const aActive = normalizeSeriesStatus(a.status) === "Continuing" ? 0 : 1;
      const bActive = normalizeSeriesStatus(b.status) === "Continuing" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return a.sort_title.localeCompare(b.sort_title);
    });
    let next = sorted;
    if (statusFilter !== null) {
      next = next.filter(
        (s) => normalizeSeriesStatus(s.status) === statusFilter,
      );
    }
    if (typeFilter !== null) {
      next = next.filter((s) => s.series_type === typeFilter);
    }
    if (hideSpecialsOnly) {
      next = next.filter((s) => !s.specials_only);
    }
    if (lower) {
      next = next.filter((s) => s.title.toLowerCase().includes(lower));
    }
    return next;
  }, [series, lower, statusFilter, typeFilter, hideSpecialsOnly]);

  // Number of specials-only series in the library — surfaced in the
  // filter chip's count so the user knows how many rows the toggle
  // would reveal.
  const specialsOnlyCount = useMemo(
    () => (series ?? []).filter((s) => s.specials_only).length,
    [series],
  );

  // Per-status counts, computed on the unfiltered set so the chip
  // labels reflect the whole library (not the active filter). This
  // gives the user a sense of how many shows fall into each bucket
  // before they pick one.
  const statusCounts = useMemo(() => {
    const counts: Record<SeriesStatus, number> = {
      Continuing: 0,
      Ended: 0,
      Upcoming: 0,
    };
    for (const s of series ?? []) {
      counts[normalizeSeriesStatus(s.status)] += 1;
    }
    return counts;
  }, [series]);

  const typeCounts = useMemo(() => {
    const counts: Record<SeriesType, number> = {
      standard: 0,
      daily: 0,
      anime: 0,
    };
    for (const s of series ?? []) {
      // Tolerate legacy rows whose decoded value somehow falls outside
      // the known set — bucket them as standard for the chip count.
      if (s.series_type in counts) {
        counts[s.series_type] += 1;
      } else {
        counts.standard += 1;
      }
    }
    return counts;
  }, [series]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="TV"
        title="Series"
        description="Television you're monitoring. Tap a card to manage seasons and search for missing episodes."
        actions={
          <>
            <Link to="/tv/wanted">
              <Button variant="secondary">View wanted</Button>
            </Link>
            <Button
              variant="secondary"
              disabled={!series || series.length === 0}
              onClick={() => setBulkOpen(true)}
              title="Set type for all series in your library"
            >
              Set type for all
            </Button>
            <Button onClick={() => setAdding(true)}>Add series</Button>
          </>
        }
      />

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter series"
          />
        </div>
        {series && (
          <span className="font-mono text-xs text-foreground/55">
            {filter || statusFilter !== null || typeFilter !== null
              ? `${filtered.length} of ${series.length}`
              : `${series.length} series`}
          </span>
        )}
      </div>

      {series && series.length > 0 && (
        <div className="space-y-2">
          <StatusFilterChips
            active={statusFilter}
            counts={statusCounts}
            onChange={setStatusFilter}
          />
          <TypeFilterChips
            active={typeFilter}
            counts={typeCounts}
            onChange={setTypeFilter}
          />
          {specialsOnlyCount > 0 && (
            <SpecialsOnlyToggle
              hide={hideSpecialsOnly}
              count={specialsOnlyCount}
              onToggle={() => setHideSpecialsOnly((v) => !v)}
            />
          )}
        </div>
      )}

      {isLoading ? (
        <Skeleton.Cards count={10} cols={5} />
      ) : !series || series.length === 0 ? (
        <EmptyState
          glyph="antenna"
          title="No series yet"
          description="Search TMDB to add your first series and start monitoring episodes."
          action={<Button onClick={() => setAdding(true)}>Add series</Button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description={`Nothing matches "${filter}".`}
          action={
            <Button variant="ghost" onClick={() => setFilter("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filtered.map((s) => (
            <SeriesCard key={s.id} series={s} />
          ))}
        </div>
      )}

      <AddSeriesModal open={adding} onClose={() => setAdding(false)} />
      <BulkSetTypeModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        seriesIds={(series ?? []).map((s) => s.id)}
      />
    </div>
  );
}

/**
 * Sonarr-style monitor presets, surfaced as a radio group on the Add
 * Series modal. The `value` is the wire format (kebab-case) — these
 * map 1:1 to `MonitorPreset` on the backend. Tooltip text is
 * deliberately one-line; longer prose is explained by the helper line
 * underneath the group.
 */
const MONITOR_PRESETS: ReadonlyArray<{
  value: MonitorPreset;
  label: string;
  hint: string;
}> = [
  {
    value: "all",
    label: "All",
    hint: "Every episode — full back-catalog grab.",
  },
  {
    value: "future",
    label: "Future",
    hint: "Only episodes airing today or later.",
  },
  {
    value: "missing",
    label: "Missing",
    hint: "Only episodes that don't have a file yet.",
  },
  {
    value: "existing",
    label: "Existing",
    hint: "Only episodes that already have a file.",
  },
  {
    value: "first-season",
    label: "First Season",
    hint: "Pilot season only — try-before-you-buy.",
  },
  {
    value: "last-season",
    label: "Last Season",
    hint: "Most recent season only.",
  },
  {
    value: "none",
    label: "None",
    hint: "Add the series but don't monitor anything yet.",
  },
];

function AddSeriesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SeriesLookupResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [addingTmdbId, setAddingTmdbId] = useState<number | null>(null);
  // Default to "future" — for ongoing shows this is what most users
  // want and it avoids pulling 800+ episodes for something like
  // Doctor Who. Backend will swap to "all" if status === Ended.
  const [monitor, setMonitor] = useState<MonitorPreset>("future");
  // Issue #65: explicit user override of the auto-detected series
  // type. `null` = "let the backend auto-detect from TMDB". The
  // search hits also carry a `suggested_type` that we surface as the
  // initial pre-pick once a user clicks a result, but for now the
  // dropdown sits at the modal level so a single search→add pair
  // doesn't need to thread per-row state.
  const [seriesType, setSeriesType] = useState<SeriesType | null>(null);

  const reset = () => {
    setQ("");
    setResults(null);
    setSearching(false);
    setAddingTmdbId(null);
    setMonitor("future");
    setSeriesType(null);
  };

  const closeAndReset = () => {
    onClose();
    window.setTimeout(reset, 200);
  };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const hits = await tvApi.lookup(q.trim());
      setResults(hits.slice(0, 12));
      // Pre-populate the dropdown from the first hit's suggestion if
      // the user hasn't already picked. The TMDB search endpoint
      // doesn't expose enough metadata to give a confident answer here
      // (genre / origin_country / runtime require the per-id detail
      // call) — the server's auto-detect at add-time is still the
      // authoritative path. This is a tiny UX nudge, no more.
      if (seriesType === null && hits.length > 0) {
        setSeriesType(hits[0].suggested_type);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "Lookup failed", "error");
    } finally {
      setSearching(false);
    }
  };

  const addMut = useMutation({
    mutationFn: ({ tmdb_id }: { tmdb_id: number }) =>
      // Send `series_type` only when the user explicitly picked one;
      // omitting lets the server's auto-detect (`auto_detect_series_type`)
      // run against the freshly-fetched TMDB payload.
      tvApi.add({
        tmdb_id,
        monitor,
        ...(seriesType ? { series_type: seriesType } : {}),
      }),
    onSuccess: (s) => {
      toast(`Added ${s.title}`, "success");
      qc.invalidateQueries({ queryKey: ["series"] });
      closeAndReset();
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : "Add failed", "error");
      setAddingTmdbId(null);
    },
  });

  return (
    <Modal
      open={open}
      onClose={closeAndReset}
      eyebrow="From TMDB"
      title="Add series"
      width="lg"
    >
      <div className="space-y-4">
        <Field label="Search">
          <div className="flex gap-2">
            <TextInput
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Severance"
            />
            <Button type="button" onClick={search} loading={searching}>
              Search
            </Button>
          </div>
        </Field>

        <Field
          label="Monitor"
          hint="We'll search for these episodes after adding. You can change individual episodes later."
        >
          <div
            role="radiogroup"
            aria-label="Monitor preset"
            className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"
          >
            {MONITOR_PRESETS.map((p) => {
              const checked = monitor === p.value;
              return (
                <label
                  key={p.value}
                  title={p.hint}
                  className={
                    "cursor-pointer rounded-md border px-2.5 py-1.5 text-center text-xs " +
                    "transition focus-within:border-accent/60 " +
                    (checked
                      ? "border-accent/70 bg-accent/10 text-foreground"
                      : "border-line bg-surface/40 text-foreground/75 hover:border-line-strong")
                  }
                >
                  <input
                    type="radio"
                    name="monitor-preset"
                    value={p.value}
                    checked={checked}
                    onChange={() => setMonitor(p.value)}
                    className="sr-only"
                  />
                  <span className="font-medium tracking-tight">{p.label}</span>
                </label>
              );
            })}
          </div>
        </Field>

        <Field
          label="Type"
          hint="Picks the parser for releases. Leave on auto-detect unless you know the show is a daily / anime."
        >
          <div
            role="radiogroup"
            aria-label="Series type"
            className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"
          >
            {/* "Auto" = let the server detect from TMDB metadata. */}
            <label
              key="auto"
              title="Detect from TMDB genres + origin"
              className={
                "cursor-pointer rounded-md border px-2.5 py-1.5 text-center text-xs " +
                "transition focus-within:border-accent/60 " +
                (seriesType === null
                  ? "border-accent/70 bg-accent/10 text-foreground"
                  : "border-line bg-surface/40 text-foreground/75 hover:border-line-strong")
              }
            >
              <input
                type="radio"
                name="series-type"
                value="__auto"
                checked={seriesType === null}
                onChange={() => setSeriesType(null)}
                className="sr-only"
              />
              <span className="font-medium tracking-tight">Auto</span>
            </label>
            {SERIES_TYPE_OPTIONS.map((t) => {
              const checked = seriesType === t.value;
              return (
                <label
                  key={t.value}
                  title={t.hint}
                  className={
                    "cursor-pointer rounded-md border px-2.5 py-1.5 text-center text-xs " +
                    "transition focus-within:border-accent/60 " +
                    (checked
                      ? "border-accent/70 bg-accent/10 text-foreground"
                      : "border-line bg-surface/40 text-foreground/75 hover:border-line-strong")
                  }
                >
                  <input
                    type="radio"
                    name="series-type"
                    value={t.value}
                    checked={checked}
                    onChange={() => setSeriesType(t.value)}
                    className="sr-only"
                  />
                  <span className="font-medium tracking-tight">{t.label}</span>
                </label>
              );
            })}
          </div>
        </Field>

        {results !== null && results.length > 0 && (
          <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
            {results.map((r) => (
              <div
                key={r.tmdb_id ?? r.title}
                className="flex items-center gap-3 rounded-md border border-line bg-surface/40 p-2.5 transition hover:border-line-strong"
              >
                {r.poster_url ? (
                  <img
                    src={r.poster_url}
                    alt=""
                    className="h-20 w-14 shrink-0 rounded object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="grid h-20 w-14 shrink-0 place-items-center rounded bg-surface-2 text-[10px] text-foreground/35">
                    no poster
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display text-base font-medium tracking-display-tight">
                    {r.title}
                  </div>
                  <div className="mt-0.5 font-mono text-xs text-foreground/55">
                    {r.year ?? "—"}
                  </div>
                  {r.overview && (
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground/70">
                      {r.overview}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  loading={addingTmdbId === r.tmdb_id}
                  disabled={!r.tmdb_id}
                  onClick={() => {
                    if (!r.tmdb_id) return;
                    setAddingTmdbId(r.tmdb_id);
                    addMut.mutate({ tmdb_id: r.tmdb_id });
                  }}
                >
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}

        {results !== null && results.length === 0 && !searching && (
          <p className="text-sm text-foreground/55">
            No results — try a different query.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Single-select status filter. Default ("all") is the leftmost chip
 * with no active styling; clicking one of `Continuing` / `Ended` /
 * `Upcoming` narrows the grid to just that bucket. Clicking the
 * already-active chip clears the filter back to "all" — matches the
 * single-select chip pattern used elsewhere in the app.
 */
function StatusFilterChips({
  active,
  counts,
  onChange,
}: {
  active: StatusFilter;
  counts: Record<SeriesStatus, number>;
  onChange: (next: StatusFilter) => void;
}) {
  const options: Array<{
    value: StatusFilter;
    label: string;
    count: number | null;
  }> = [
    { value: null, label: "All", count: null },
    { value: "Continuing", label: "Continuing", count: counts.Continuing },
    { value: "Ended", label: "Ended", count: counts.Ended },
    { value: "Upcoming", label: "Upcoming", count: counts.Upcoming },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((opt) => {
        const isActive = active === opt.value;
        return (
          <button
            key={opt.value ?? "all"}
            type="button"
            onClick={() =>
              // Clicking the active chip clears (only meaningful for
              // the three real buckets — clicking "All" while active
              // is a no-op).
              onChange(isActive && opt.value !== null ? null : opt.value)
            }
            className={cn(
              "focus-ring inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] transition",
              isActive
                ? "border-accent/50 bg-accent/10 text-accent"
                : "border-line bg-surface-2/50 text-foreground/65 hover:border-line-strong hover:text-foreground",
            )}
            aria-pressed={isActive}
          >
            <span>{opt.label}</span>
            {opt.count !== null && (
              <span className="tabular-nums opacity-65">{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Issue #65: type filter chips, paired with {@link StatusFilterChips}
 * above. Same single-select / "click active to clear" semantics.
 * Default ("all") shows everything regardless of type.
 */
function TypeFilterChips({
  active,
  counts,
  onChange,
}: {
  active: TypeFilter;
  counts: Record<SeriesType, number>;
  onChange: (next: TypeFilter) => void;
}) {
  const options: Array<{
    value: TypeFilter;
    label: string;
    count: number | null;
  }> = [
    { value: null, label: "Any type", count: null },
    { value: "standard", label: "Standard", count: counts.standard },
    { value: "daily", label: "Daily", count: counts.daily },
    { value: "anime", label: "Anime", count: counts.anime },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((opt) => {
        const isActive = active === opt.value;
        return (
          <button
            key={opt.value ?? "all"}
            type="button"
            onClick={() =>
              onChange(isActive && opt.value !== null ? null : opt.value)
            }
            className={cn(
              "focus-ring inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] transition",
              isActive
                ? "border-accent/50 bg-accent/10 text-accent"
                : "border-line bg-surface-2/50 text-foreground/65 hover:border-line-strong hover:text-foreground",
            )}
            aria-pressed={isActive}
          >
            <span>{opt.label}</span>
            {opt.count !== null && (
              <span className="tabular-nums opacity-65">{opt.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Issue #65: bulk "set type for all" admin modal. Sends one
 * `POST /api/v1/tv/series-type/bulk` for every series id currently
 * known to the page (the unfiltered list) — the issue scope was
 * deliberately limited to "set for all" rather than per-row
 * checkboxes, so we treat the modal as a library-wide hammer.
 */
function BulkSetTypeModal({
  open,
  onClose,
  seriesIds,
}: {
  open: boolean;
  onClose: () => void;
  seriesIds: number[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [target, setTarget] = useState<SeriesType>("standard");

  const mut = useMutation({
    mutationFn: () =>
      tvApi.bulkSetSeriesType({ series_ids: seriesIds, series_type: target }),
    onSuccess: (resp) => {
      toast(
        `Updated ${resp.updated} series → ${SERIES_TYPE_LABEL[target]}`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["series"] });
      onClose();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Bulk update failed", "error"),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Bulk admin"
      title="Set type for all series"
      width="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-foreground/70">
          Apply one series type to every show in your library.
          {seriesIds.length > 0 ? (
            <>
              {" "}
              <span className="font-mono text-foreground/55">
                {seriesIds.length} series will be updated.
              </span>
            </>
          ) : null}
        </p>

        <Field label="Type">
          <div
            role="radiogroup"
            aria-label="Bulk series type"
            className="grid grid-cols-3 gap-1.5"
          >
            {SERIES_TYPE_OPTIONS.map((t) => {
              const checked = target === t.value;
              return (
                <label
                  key={t.value}
                  title={t.hint}
                  className={
                    "cursor-pointer rounded-md border px-2.5 py-1.5 text-center text-xs " +
                    "transition focus-within:border-accent/60 " +
                    (checked
                      ? "border-accent/70 bg-accent/10 text-foreground"
                      : "border-line bg-surface/40 text-foreground/75 hover:border-line-strong")
                  }
                >
                  <input
                    type="radio"
                    name="bulk-series-type"
                    value={t.value}
                    checked={checked}
                    onChange={() => setTarget(t.value)}
                    className="sr-only"
                  />
                  <span className="font-medium tracking-tight">{t.label}</span>
                </label>
              );
            })}
          </div>
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={mut.isPending}
            disabled={seriesIds.length === 0}
            onClick={() => mut.mutate()}
          >
            Apply to {seriesIds.length} series
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function SeriesCard({ series }: { series: Series }) {
  const status = normalizeSeriesStatus(series.status);
  return (
    <Link
      to="/tv/$id"
      params={{ id: String(series.id) }}
      className="group relative overflow-hidden rounded-md border border-line bg-surface/30 transition hover:border-line-strong"
    >
      <div className="relative">
        {series.poster_url ? (
          <img
            src={series.poster_url}
            alt=""
            className="aspect-[2/3] w-full object-cover transition group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div className="grid aspect-[2/3] place-items-center bg-surface-2 text-xs text-foreground/35">
            no poster
          </div>
        )}
        <div className="absolute right-2 top-2 flex flex-col items-end gap-1.5">
          <SeriesCardStatusChip status={status} />
          {!series.monitored && (
            <Status variant="chip" tone="neutral">
              off
            </Status>
          )}
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background via-background/80 to-transparent"
        />
      </div>
      <div className="p-3">
        <div className="line-clamp-1 font-display text-sm font-medium leading-tight tracking-display-tight">
          {series.title}
        </div>
        <div className="mt-1 flex items-center justify-between font-mono text-[11px] text-foreground/55">
          <span className="tabular-nums">
            {series.first_air_date?.slice(0, 4) ?? "—"}
          </span>
        </div>
      </div>
    </Link>
  );
}

/**
 * Compact card-corner chip mirroring the toned palette used in the
 * SeriesDetail header. We use the `live` shorthand for `Continuing`
 * here (the card chip is space-constrained) and full words for the
 * other two. See {@link SeriesStatus} for the bucket definitions.
 */
function SeriesCardStatusChip({ status }: { status: SeriesStatus }) {
  if (status === "Continuing") {
    return (
      <Status variant="chip" tone="good" pulse>
        live
      </Status>
    );
  }
  if (status === "Ended") {
    return (
      <Status variant="chip" tone="neutral">
        ended
      </Status>
    );
  }
  return (
    <Status variant="chip" tone="info">
      upcoming
    </Status>
  );
}

/**
 * Issue #66: chip-style toggle that hides series with no real seasons
 * (everything in S00). Shown only when at least one such series exists
 * — there's no point teasing a filter that has nothing to filter. The
 * label reflects the *current* state ("Hide" while active, "Show" while
 * inactive) so a quick scan tells the user what clicking will do.
 */
function SpecialsOnlyToggle({
  hide,
  count,
  onToggle,
}: {
  hide: boolean;
  count: number;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "focus-ring inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] transition",
        hide
          ? "border-accent/50 bg-accent/10 text-accent"
          : "border-line bg-surface-2/50 text-foreground/65 hover:border-line-strong hover:text-foreground",
      )}
      aria-pressed={hide}
      title={
        hide
          ? "Specials-only series are hidden from the list"
          : "Specials-only series are visible in the list"
      }
    >
      <span>{hide ? "Hide" : "Show"} specials-only</span>
      <span className="tabular-nums opacity-65">{count}</span>
    </button>
  );
}
